/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { StyleSheet, View, RefreshControl } from 'react-native';

import * as Layout from '@components/screen/Layout';
import CardTotal from '@/screens/home/components/CardTotal';
import Activities from '@/screens/home/components/Activities';
import VisitDetail from '@/screens/home/components/snippets/VisitDetail';
import Task from '@/screens/home/components/Task';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';
import {
  getDashboardCount,
  getDashboardSum,
  getBeautyAdvisorDashboard,
  getPromotorDashboard,
} from '@api/ms-b2b';
import { setSavedFilter, isVn } from '@global/helpers';
import ScrollContainer from '@components/global/ScrollContainer';
import { isWeb, isNative } from '@global/screens';
import Colors from '@global/colors';

const ADMIN_ROLES = [
  'superadmin',
  'distributor_manager',
  'general_manager',
  'vice_president',
  'general',
];

const MANAGER_ROLES = [
  'finance_supervisor',
  'sales_admin',
  'branch_head',
  'sales_supervisor',
  'national_key_account_manager',
  'regional_distributor_manager',
];

const INTERNAL_PIC_ROLES = [
  'regional_manager',
  'group_key_account_manager',
  'regional_support',
  'key_account_manager',
];

const USER_ROLES = ['beauty_advisor', 'salesman', 'merchandiser', 'promotor'];

const DASHBOARD_TYPES = {
  CUSTOMERS: 'customers',
  ACTIVITIES: 'activities',
  ORDERS: 'orders',
  SALES_INVOICE: 'sales-invoice',
};

const HomeScreen = (props) => {
  const today = new Date();
  const scrollRef = useRef(null);
  const userId = useSelector((state) => state.auth.userId);
  const auth = useSelector((state) => state.auth);
  const role = auth.user?.role?.code || '';

  const getTasks = useApi(MsB2b.getTasks);
  const getActivities = useApi(MsB2b.getActivities);

  const [assigned, setAssigned] = useState({
    title: 'Assigned Customer',
    value: 0,
  });
  const [planned, setPlanned] = useState({
    title: 'Planned Activity',
    value: 0,
  });
  const [salesOrder, setSalesOrder] = useState({
    title: 'Total Sales Order',
    value: 0,
    withSum: true,
    sum: 0,
  });
  const [salesInvoice, setSalesInvoice] = useState({
    title: 'Total Sales Invoice',
    value: 0,
    withSum: true,
    sum: 0,
  });
  const [promotorSellOutNew, setPromotorSellOutNew] = useState({
    title: 'Sell Out New Brand',
    value: '',
    withSum: true,
    sum: 0,
  });
  const [activeMobileTab, setActiveMobileTab] = useState('');
  const [cardTotalLoading, setCardTotalLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const assignedCustomersFilter = useMemo(() => {
    if (!role) {
      return {};
    }

    const salesTeam = auth.user.sales_team || [];
    const filter = {};

    if (ADMIN_ROLES.includes(role)) {
      filter.status = 'approved';
    } else if (MANAGER_ROLES.includes(role) && salesTeam.length > 0) {
      filter['sales_team.id'] = { $in: salesTeam.map((x) => x.id) };
    } else if (INTERNAL_PIC_ROLES.includes(role)) {
      filter['$or'] = [
        { 'key_account_manager.id': auth.userId },
        { 'account_manager.id': auth.userId },
      ];
    } else if (role === 'salesman') {
      filter['user.id'] = auth.userId;
    }

    return filter;
  }, [auth, role]);

  const resetCardStates = () => {
    const isBeautyAdvisor = role === 'beauty_advisor';
    const isPromotor = role === 'promotor';

    if (isPromotor) {
      setAssigned({ title: 'Total Visit Instore', value: 0 });
      setPlanned({ title: 'Total Visit Outstore', value: 0 });
      setSalesOrder({
        title: 'Total Visit Online',
        value: 0,
        withSum: false,
        sum: 0,
      });
      setSalesInvoice({
        title: 'Sell Out Existing Brand',
        value: '',
        withSum: true,
        sum: 0,
      });
      setPromotorSellOutNew({
        title: 'Sell Out New Brand',
        value: '',
        withSum: true,
        sum: 0,
      });
      return;
    }

    setAssigned({
      title: isBeautyAdvisor ? 'Number Of Visit' : 'Assigned Customer',
      value: 0,
    });

    setPlanned({
      title: 'Planned Activity',
      value: 0,
    });

    setSalesOrder({
      title: isBeautyAdvisor ? 'Total Sell In' : 'Total Sales Order',
      value: isBeautyAdvisor ? '' : 0,
      withSum: true,
      sum: 0,
    });

    setSalesInvoice({
      title: isBeautyAdvisor ? 'Total Sell Out' : 'Total Sales Invoice',
      value: 0,
      withSum: true,
      sum: 0,
    });
  };

  const getSumAmount = async (type, fieldKey) => {
    try {
      const resAmount = await getDashboardSum(
        { params: { fields: fieldKey } },
        type,
      );
      return resAmount?.data?.data || 0;
    } catch (error) {
      console.error(`Error fetching sum for ${type}:`, error);
      return 0;
    }
  };

  const fetchPromotorDashboard = async () => {
    try {
      const response = await getPromotorDashboard({});
      const data = response?.data?.data || {};

      setAssigned({
        title: 'Total Visit Instore',
        value: data.total_visit_instore || 0,
      });
      setPlanned({
        title: 'Total Visit Outstore',
        value: data.total_visit_outstore || 0,
      });
      setSalesOrder({
        title: 'Total Visit Online',
        value: data.total_visit_online || 0,
        withSum: false,
        sum: 0,
      });
      setSalesInvoice({
        title: 'Sell Out Existing Brand',
        value: '',
        withSum: true,
        sum: data.sell_out_existing_brand || 0,
      });
      setPromotorSellOutNew({
        title: 'Sell Out New Brand',
        value: '',
        withSum: true,
        sum: data.sell_out_new_brand || 0,
      });
    } catch (error) {
      console.error('Error fetching promotor dashboard:', error);
    }
  };

  const fetchBeautyAdvisorDashboard = async () => {
    try {
      const response = await getBeautyAdvisorDashboard({});
      const data = response?.data?.data || {};

      setAssigned({
        title: 'Number Of Visit',
        value: data.number_visit || 0,
      });

      try {
        const activitiesResponse = await getDashboardCount(
          {},
          DASHBOARD_TYPES.ACTIVITIES,
        );
        setPlanned({
          title: 'Planned Activity',
          value: activitiesResponse?.data?.data || 0,
        });
      } catch (error) {
        console.error('Error fetching planned activities:', error);
      }

      setSalesOrder({
        title: 'Total Sell In',
        value: '',
        withSum: true,
        sum: data.total_sell_in || 0,
      });

      setSalesInvoice({
        title: 'Total Sell Out',
        value: '',
        withSum: true,
        sum: data.total_sell_out || 0,
      });
    } catch (error) {
      console.error('Error fetching beauty advisor dashboard:', error);
    }
  };

  const fetchRegularDashboard = async () => {
    const dashboardTypes = [
      DASHBOARD_TYPES.CUSTOMERS,
      DASHBOARD_TYPES.ACTIVITIES,
      DASHBOARD_TYPES.ORDERS,
      DASHBOARD_TYPES.SALES_INVOICE,
    ];

    for (const type of dashboardTypes) {
      try {
        const response = await getDashboardCount({}, type);
        const count = response?.data?.data || 0;

        switch (type) {
          case DASHBOARD_TYPES.CUSTOMERS:
            setAssigned({
              title: 'Assigned Customer',
              value: count,
            });
            break;

          case DASHBOARD_TYPES.ACTIVITIES:
            setPlanned({
              title: 'Planned Activity',
              value: count,
            });
            break;

          case DASHBOARD_TYPES.ORDERS: {
            const orderSum =
              count > 0 ? await getSumAmount(type, 'total_amount') : 0;
            setSalesOrder({
              title: 'Total Sales Order',
              value: count,
              sum: orderSum,
              withSum: true,
            });
            break;
          }

          case DASHBOARD_TYPES.SALES_INVOICE: {
            const invoiceSum =
              count > 0 ? await getSumAmount(type, 'total_amount') : 0;
            setSalesInvoice({
              title: 'Total Sales Invoice',
              value: count,
              sum: invoiceSum,
              withSum: true,
            });
            break;
          }
        }
      } catch (error) {
        console.error(`Error fetching dashboard count for ${type}:`, error);
      }
    }
  };

  const getTotalCount = async () => {
    resetCardStates();
    setCardTotalLoading(true);

    try {
      if (role === 'beauty_advisor') {
        await fetchBeautyAdvisorDashboard();
      } else if (role === 'promotor') {
        await fetchPromotorDashboard();
      } else {
        await fetchRegularDashboard();
      }
    } finally {
      setCardTotalLoading(false);
    }
  };

  const onRefresh = async () => {
    try {
      setRefreshing(true);
      fetchActivitiesAndTasks();
      getTotalCount();
    } finally {
      setRefreshing(false);
    }
  };

  const navigateToCustomers = async () => {
    if (ADMIN_ROLES.includes(role)) {
      const filter = JSON.parse(JSON.stringify(assignedCustomersFilter));
      await setSavedFilter({ filters: {} }, false, 'CustomerScreen');
      await setSavedFilter({ filters: filter }, false, 'CustomerScreen');
    } else if (INTERNAL_PIC_ROLES.includes(role)) {
      await setSavedFilter({ filters: {} }, false, 'CustomerScreen');
      await setSavedFilter(
        {
          filters: {
            ...assignedCustomersFilter,
            status: 'approved',
            is_active: true,
          },
        },
        false,
        'CustomerScreen',
      );
    }
    props.navigation.navigate('CustomerNavigator', {
      screen: 'CustomerScreen',
      params: { isHome: true },
    });
  };

  const navigateToSalesActivity = () => {
    props.navigation.navigate('SalesActivityNavigator', {
      screen: 'SalesActivityScreen',
    });
  };

  const navigateToPlannedActivities = () => {
    props.navigation.navigate('SalesActivityNavigator', {
      screen: 'SalesActivityListScreen',
      params: {
        type: 'planned_activities',
        titleScreen: 'Planned Activities',
        isHome: true,
      },
    });
  };

  const navigateToPastActivities = () => {
    props.navigation.navigate('SalesActivityNavigator', {
      screen: 'SalesActivityListScreen',
      params: {
        type: 'past_activities',
        titleScreen: 'Past Activities',
        isHome: true,
      },
    });
  };

  const navigateToSalesOrder = (isInvoice = false) => {
    props.navigation.navigate('SalesOrderNavigator', {
      screen: 'SalesOrderScreen',
      params: {
        titleScreen: 'Home',
        isHome: true,
        ...(isInvoice && { isSalesInvoiceDashboard: true }),
      },
    });
  };

  const cardPressed = async (card) => {
    const title = card.title || '';

    if (title === 'Assigned Customer') {
      await navigateToCustomers();
    } else if (title === 'Number Of Visit') {
      navigateToSalesActivity();
    } else if (title === 'Planned Activity') {
      navigateToPlannedActivities();
    } else if (title === 'Total Sales Order' || title === 'Total Sell In') {
      navigateToSalesOrder(false);
    } else if (title === 'Total Sales Invoice') {
      navigateToSalesOrder(true);
    } else if (title === 'Total Sell Out') {
      navigateToPastActivities();
    }
  };

  const fetchActivitiesAndTasks = () => {
    const source = isVn() ? 'b2b_vn' : 'b2b';

    getTasks.request({
      params: {
        filter: {
          is_completed: { $ne: true },
          users: { $elemMatch: { id: userId } },
          is_active_for: { $in: [source] },
        },
        limit: 3,
        sort: '-created_at',
      },
    });

    const activitiesFilter = {
      status: 'approved',
      $or: [
        { visited_at: { $gte: today.toISOString() } },
        { finished_at: { $gte: today.toISOString() } },
      ],
    };

    if (USER_ROLES.includes(role)) {
      activitiesFilter['user.id'] = userId;
    }

    getActivities.request({
      params: {
        filter: activitiesFilter,
        limit: 3,
        sort: 'visited_at',
      },
    });
  };

  useEffect(() => {
    setCardTotalLoading(true);
    resetCardStates();
  }, [role, userId]);

  useEffect(() => {
    let timer;
    const fetchData = () => {
      if (!userId || !role) {
        return;
      }
      fetchActivitiesAndTasks();
      timer = setTimeout(() => {
        getTotalCount();
      }, 0);
    };

    fetchData();

    const unsubscribe = props.navigation.addListener('focus', fetchData);

    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, [userId, role]);

  useEffect(() => {
    if (props?.route?.params?.section) {
      if (!isWeb) {
        const section = props.route.params.section;
        setActiveMobileTab(section === 'sales_team' ? 'sales_team' : '');
      } else {
        props.navigation.setParams({ section: '' });
      }
    }
  }, [props.route]);

  useEffect(() => {
    if (isNative && scrollRef?.current) {
      scrollRef.current.scrollTo({ x: 0, animated: true });
    }
  }, [activeMobileTab]);

  const renderActivities = () => {
    if (getActivities.loading && !getActivities.error) {
      return null;
    }

    return (
      <Activities
        data={getActivities.data}
        navigation={props.navigation}
        style={{
          height: 290,
          marginRight: isWeb ? 30 : 0,
          ...(!isWeb ? { maxHeight: 267 } : { flex: 1 }),
        }}
      />
    );
  };

  const renderTasks = () => {
    if (getTasks.loading && !getTasks.error) {
      return null;
    }

    return (
      <Task
        navigation={props.navigation}
        data={getTasks.data}
        style={{
          height: 290,
          ...(!isWeb ? { maxHeight: 267 } : { flex: 1 }),
        }}
      />
    );
  };

  return (
    <Layout.Container
      style={!isWeb ? styles.mobileContainer : styles.containerWeb}>
      <ScrollContainer
        ref={scrollRef}
        style={styles.containerScrollWeb}
        refreshControl={
          !isWeb ? (
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          ) : null
        }
        showsVerticalScrollIndicator>
        {(isWeb || !activeMobileTab) && (
          <>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}>
              <CardTotal
                data={
                  role === 'promotor'
                    ? [
                        assigned,
                        planned,
                        salesOrder,
                        salesInvoice,
                        promotorSellOutNew,
                      ]
                    : [assigned, planned, salesOrder, salesInvoice]
                }
                onPress={cardPressed}
                loading={cardTotalLoading}
              />
            </View>
            <View style={{ flexDirection: isWeb ? 'row' : 'column' }}>
              {renderActivities()}
              {renderTasks()}
            </View>
          </>
        )}
      </ScrollContainer>
      <VisitDetail />
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  mobileContainer: {
    backgroundColor: Colors.grey5,
    paddingTop: 15,
    paddingBottom: 10,
    paddingHorizontal: 0,
  },
  containerWeb: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingHorizontal: 0,
  },
  containerScrollWeb: {
    paddingTop: isWeb ? 30 : 0,
    paddingBottom: 30,
    paddingHorizontal: isWeb ? 30 : 15,
  },
});

export default HomeScreen;
